import React, { useRef, useState, useEffect, useCallback } from "react"
import { UploadCloud, X, ImageOff } from "lucide-react"

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"]
const MAX_BYTES = 10 * 1024 * 1024

// Reusable image gallery uploader for forms.
//
// Props:
//   existing   — [{ id, url, filename }] already-attached images (edit mode)
//   onChange   — called with { newFiles: File[], removedIds: number[] } on any change
//   max        — max total images (existing-kept + new), default 30
//   label      — accessible label / heading
//
// The parent submits newFiles (append) and removedIds (purge) to the API.
export function ImageUploader({ existing = [], onChange, max = 30, label = "Images" }) {
  const inputRef = useRef(null)
  const [removedIds, setRemovedIds] = useState([])
  const [newItems, setNewItems] = useState([]) // { file, url }
  const [error, setError] = useState(null)

  // Revoke object URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => newItems.forEach((it) => URL.revokeObjectURL(it.url))
  }, [newItems])

  const emit = useCallback(
    (items, removed) => onChange?.({ newFiles: items.map((i) => i.file), removedIds: removed }),
    [onChange]
  )

  const keptExisting = existing.filter((e) => !removedIds.includes(e.id))
  const total = keptExisting.length + newItems.length

  function addFiles(fileList) {
    const incoming = Array.from(fileList || [])
    if (incoming.length === 0) return

    const accepted = []
    for (const file of incoming) {
      if (!ALLOWED.includes(file.type)) {
        setError(`${file.name}: must be JPEG, PNG, WebP or GIF`)
        continue
      }
      if (file.size > MAX_BYTES) {
        setError(`${file.name}: must be smaller than 10 MB`)
        continue
      }
      accepted.push({ file, url: URL.createObjectURL(file) })
    }

    if (total + accepted.length > max) {
      setError(`You can attach at most ${max} images`)
      return
    }
    if (accepted.length === 0) return

    setError(null)
    const next = [...newItems, ...accepted]
    setNewItems(next)
    emit(next, removedIds)
  }

  function removeExisting(id) {
    const next = [...removedIds, id]
    setRemovedIds(next)
    emit(newItems, next)
  }

  function removeNew(index) {
    const item = newItems[index]
    if (item) URL.revokeObjectURL(item.url)
    const next = newItems.filter((_, i) => i !== index)
    setNewItems(next)
    emit(next, removedIds)
  }

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files) }}
        className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center transition-colors hover:border-slate-400"
        aria-label={`Upload ${label}`}
      >
        <UploadCloud className="h-6 w-6 text-slate-400" />
        <span className="text-[13px] text-slate-600">
          Drop images or <span className="font-medium text-blue-700">browse</span>
        </span>
        <span className="text-[11px] text-slate-400">JPEG, PNG, WebP, GIF · up to 10 MB each</span>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED.join(",")}
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = "" }}
        />
      </div>

      {error && <p className="text-[12px] text-red-600">{error}</p>}

      {total === 0 ? (
        <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-[12px] text-slate-400">
          <ImageOff className="h-4 w-4" />
          No images yet
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {keptExisting.map((img) => (
            <Thumb key={`e-${img.id}`} src={img.url} alt={img.filename} onRemove={() => removeExisting(img.id)} />
          ))}
          {newItems.map((it, i) => (
            <Thumb key={`n-${i}`} src={it.url} alt={it.file.name} badge="New" onRemove={() => removeNew(i)} />
          ))}
        </div>
      )}
    </div>
  )
}

function Thumb({ src, alt, badge, onRemove }) {
  return (
    <div className="group relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-50">
      <img src={src} alt={alt} className="h-full w-full object-cover" />
      {badge && (
        <span className="absolute left-1 top-1 rounded bg-blue-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {badge}
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${alt || "image"}`}
        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-sm transition-opacity hover:bg-white hover:text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
